const selectorSegmentPattern = /^([^\[\]]+)\[([^=\]]+)=([^\]]+)\]$/;
const indexSegmentPattern = /^([^\[\]]+)\[(\d+)\]$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const splitPath = (path: string): string[] => {
  const segments: string[] = [];
  let current = "";
  let bracketDepth = 0;
  let quote: "\"" | "'" | undefined;

  for (const character of path) {
    if (quote) {
      current += character;
      if (character === quote) quote = undefined;
      continue;
    }
    if (character === "\"" || character === "'") {
      quote = character;
      current += character;
      continue;
    }
    if (character === "[") bracketDepth += 1;
    if (character === "]") bracketDepth -= 1;
    if (character === "." && bracketDepth === 0) {
      segments.push(current);
      current = "";
    } else {
      current += character;
    }
  }
  if (current) segments.push(current);
  return segments;
};

const resolveSelectorValue = (
  expression: string,
  context: Record<string, unknown>,
): { found: boolean; value?: unknown } => {
  const value = expression.trim();
  if (value.startsWith("this.")) {
    const path = value.slice("this.".length).split(".");
    let current: unknown = context;
    for (const segment of path) {
      if (!isRecord(current) || !Object.prototype.hasOwnProperty.call(current, segment)) {
        return { found: false };
      }
      current = current[segment];
    }
    return { found: true, value: current };
  }
  if (value.startsWith("$")) {
    const name = value.slice(1);
    return Object.prototype.hasOwnProperty.call(context, name)
      ? { found: true, value: context[name] }
      : { found: false };
  }
  if ((value.startsWith("\"") && value.endsWith("\"")) || (value.startsWith("'") && value.endsWith("'"))) {
    return { found: true, value: value.slice(1, -1) };
  }
  if (value === "true") return { found: true, value: true };
  if (value === "false") return { found: true, value: false };
  if (value === "null") return { found: true, value: null };
  if (value !== "" && Number.isFinite(Number(value))) return { found: true, value: Number(value) };
  return { found: true, value };
};

/**
 * Converts selector paths such as `columns[id=this.id].field` into ordinary
 * index paths such as `columns[3].field` for the supplied object.
 */
export const resolveAdapterPathSelectors = (
  source: unknown,
  path: string,
  context: Record<string, unknown> = {},
): string | undefined => {
  const segments = splitPath(path);
  if (!segments.some((segment) => selectorSegmentPattern.test(segment))) return path;

  const resolved: string[] = [];
  let current = source;

  for (const segment of segments) {
    const selector = segment.match(selectorSegmentPattern);
    if (selector) {
      const [, collectionKey, matchKey, expression] = selector;
      if (!isRecord(current) || !Array.isArray(current[collectionKey])) return undefined;
      const selectorValue = resolveSelectorValue(expression, context);
      if (!selectorValue.found) return undefined;
      const collection = current[collectionKey] as unknown[];
      const index = collection.findIndex(
        (item) => isRecord(item) && Object.is(item[matchKey], selectorValue.value),
      );
      if (index < 0) return undefined;
      resolved.push(`${collectionKey}[${index}]`);
      current = collection[index];
      continue;
    }

    const indexed = segment.match(indexSegmentPattern);
    if (indexed) {
      const [, collectionKey, rawIndex] = indexed;
      const index = Number(rawIndex);
      resolved.push(segment);
      current = isRecord(current) && Array.isArray(current[collectionKey])
        ? current[collectionKey][index]
        : undefined;
      continue;
    }

    resolved.push(segment);
    current = isRecord(current) ? current[segment] : undefined;
  }

  return resolved.join(".");
};
